import { ReactNode } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ArrowDown, ArrowUp, ChevronDown, Filter } from "lucide-react";
import { classNames } from "../Utils/classNames.ts";

export type ColumnSortDirection = "asc" | "desc";

const DEFAULT_SORT_LABELS = { asc: "Crescente", desc: "Decrescente" };

function SortOption({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition",
        isActive
          ? "bg-blue-50 font-medium text-blue-700"
          : "text-gray-700 hover:bg-gray-50",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

export function TableColumnHeader({
  label,
  sortDirection,
  onSort,
  sortLabels = DEFAULT_SORT_LABELS,
  isFiltered = false,
  renderFilter,
  isPanelWide = false,
}: {
  label: string;
  sortDirection?: ColumnSortDirection;
  onSort?: (direction: ColumnSortDirection) => void;
  sortLabels?: { asc: string; desc: string };
  isFiltered?: boolean;
  renderFilter?: (close: () => void) => ReactNode;
  isPanelWide?: boolean;
}) {
  if (!onSort && !renderFilter) return <span>{label}</span>;

  return (
    <Popover>
      <PopoverButton
        className={classNames(
          "group inline-flex items-center gap-1 rounded-md px-1.5 py-1 -mx-1.5 text-left uppercase transition hover:bg-gray-200/60 focus:outline-none data-[open]:bg-gray-200/60 dark:hover:bg-gray-600 dark:data-[open]:bg-gray-600",
          isFiltered ? "text-blue-700 dark:text-blue-400" : undefined,
        )}
      >
        <span>{label}</span>
        {sortDirection === "asc" && <ArrowUp className="w-3.5 h-3.5" />}
        {sortDirection === "desc" && <ArrowDown className="w-3.5 h-3.5" />}
        {isFiltered && <Filter className="w-3.5 h-3.5 fill-current" />}
        <ChevronDown className="w-3.5 h-3.5 opacity-40 transition-transform group-data-[open]:rotate-180" />
      </PopoverButton>
      <PopoverPanel
        transition
        anchor="bottom start"
        className={classNames(
          "z-50 max-w-[calc(100vw-2rem)] rounded-lg bg-white p-3 text-left normal-case shadow-lg ring-1 ring-black/5 [--anchor-gap:6px] transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0",
          isPanelWide ? "w-[28rem]" : "w-72",
        )}
      >
        {({ close }) => (
          <div className="flex flex-col gap-3">
            {onSort && (
              <div className="flex flex-col gap-1">
                <p className="px-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Ordina
                </p>
                <SortOption
                  label={sortLabels.asc}
                  icon={<ArrowUp className="w-4 h-4" />}
                  isActive={sortDirection === "asc"}
                  onClick={() => {
                    onSort("asc");
                    close();
                  }}
                />
                <SortOption
                  label={sortLabels.desc}
                  icon={<ArrowDown className="w-4 h-4" />}
                  isActive={sortDirection === "desc"}
                  onClick={() => {
                    onSort("desc");
                    close();
                  }}
                />
              </div>
            )}
            {onSort && renderFilter && <hr className="border-gray-100" />}
            {renderFilter && (
              <div className="flex flex-col gap-2">
                <p className="px-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Filtra
                </p>
                {renderFilter(close)}
              </div>
            )}
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
}
